/**
 * Adresse saisie à la création d'un client (caisse, tableau de bord) — 13/10/2026.
 *
 * Avant : un seul champ de texte libre (`postal_address`). Enfact le découpait
 * aux virgules pour retrouver rue / code postal / ville — sans virgules, tout
 * partait en « rue », code postal et ville vides sur la facture — et une
 * livraison n'en tirait rien (pas d'adresse localisée, invisible de la tournée).
 * Désormais : trois champs, enregistrés comme une vraie adresse du client
 * (localisée à la saisie), et recopiés en « rue, CP, ville » dans
 * `postal_address` pour ce qui lit encore l'ancien champ (synchronisation Enfact).
 */
export interface AdresseSaisie {
  rue: string;
  cp: string;
  ville: string;
}

export const ADRESSE_VIDE: AdresseSaisie = { rue: '', cp: '', ville: '' };

const propre = (a: AdresseSaisie): AdresseSaisie => ({ rue: a.rue.trim(), cp: a.cp.trim(), ville: a.ville.trim() });

/** Belgique (4 chiffres) par défaut ; 5 chiffres = France (clients frontaliers). */
export const paysDuCodePostal = (cp: string) => (/^\d{5}$/.test(cp.trim()) ? 'FR' : 'BE');

/** null = rien à redire (adresse complète, ou pas d'adresse du tout). */
export function verifierAdresse(a: AdresseSaisie): string | null {
  const { rue, cp, ville } = propre(a);
  if (!rue && !cp && !ville) return null;
  if (!rue || !cp || !ville) return 'Adresse incomplète : rue et numéro, code postal et ville vont ensemble';
  if (!/^\d{4,5}$/.test(cp)) return 'Code postal : 4 chiffres (5 pour la France)';
  return null;
}

export const adresseRenseignee = (a: AdresseSaisie) => {
  const { rue, cp, ville } = propre(a);
  return !!(rue && cp && ville);
};

/** L'ancien champ texte, au format que la synchronisation Enfact sait relire. */
export function adresseEnUneLigne(a: AdresseSaisie): string {
  if (!adresseRenseignee(a)) return '';
  const { rue, cp, ville } = propre(a);
  return `${rue}, ${cp}, ${ville}`;
}

/** Corps de `POST /customers/:id/addresses` : adresse par défaut, facture ET livraison. */
export function corpsAdresse(a: AdresseSaisie) {
  const { rue, cp, ville } = propre(a);
  return { type: 'delivery', street: rue, postal_code: cp, city: ville, country: paysDuCodePostal(cp), is_default: true };
}
